import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { FileText, Plus, Save, Trash2, X, Eye, EyeOff, GripVertical, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import LanguageTabs, { useAvailableLanguages } from '../../components/admin/LanguageTabs';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDocuments = () => {
  const { getAuthHeader } = useAdminAuth();
  const { languages, loading: languagesLoading, defaultLanguage } = useAvailableLanguages();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDoc, setEditingDoc] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('pl');
  const [previewMode, setPreviewMode] = useState(false);

  // Initialize empty document with all available languages
  const getEmptyDocument = () => {
    const titleObj = {};
    const contentObj = {};
    const slugObj = {};
    languages.forEach(lang => {
      titleObj[lang.code] = '';
      contentObj[lang.code] = '';
      slugObj[lang.code] = '';
    });
    return {
      slug: slugObj,
      title: titleObj,
      content: contentObj,
      isPublished: false,
      showInContact: false,
      order: 0
    };
  };
  
  // Normalize slug from string to object (for legacy documents)
  const normalizeSlug = (slug) => {
    if (typeof slug === 'string') {
      return { pl: slug, en: slug, uk: slug, de: slug };
    }
    return slug || { pl: '', en: '', uk: '', de: '' };
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/documents`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Nie udało się pobrać dokumentów');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Normalize slug to object format
    const slugObj = normalizeSlug(editingDoc.slug);
    
    if (!slugObj.pl || !editingDoc.title?.pl) {
      toast.error('Slug polski i tytuł polski są wymagane');
      return;
    }

    // Prepare document with normalized slug
    const docToSave = {
      ...editingDoc,
      slug: slugObj
    };

    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/documents`, docToSave, {
          headers: getAuthHeader()
        });
        toast.success('Dokument utworzony');
      } else {
        await axios.put(`${API}/api/admin/documents/${editingDoc.document_id}`, docToSave, {
          headers: getAuthHeader()
        });
        toast.success('Dokument zaktualizowany');
      }
      setEditingDoc(null);
      setIsCreating(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać');
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten dokument?')) return;

    try {
      await axios.delete(`${API}/api/admin/documents/${documentId}`, {
        headers: getAuthHeader()
      });
      toast.success('Dokument usunięty');
      fetchDocuments();
    } catch (error) {
      toast.error('Nie udało się usunąć dokumentu');
    }
  };

  const handleTogglePublished = async (doc) => {
    try {
      await axios.put(`${API}/api/admin/documents/${doc.document_id}`, 
        { isPublished: !doc.isPublished },
        { headers: getAuthHeader() }
      );
      toast.success(doc.isPublished ? 'Dokument ukryty' : 'Dokument opublikowany');
      fetchDocuments();
    } catch (error) {
      toast.error('Nie udało się zmienić statusu');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[ąćęłńóśźż]/g, c => ({
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 
        'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z'
      }[c] || c))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Simple Markdown to HTML converter
  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold mt-6 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-8 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^\- (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      .replace(/\n/g, '<br/>');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dokumenty</h1>
          <p className="text-gray-400">Regulaminy, polityka prywatności i inne dokumenty</p>
        </div>
        <Button
          onClick={() => { setEditingDoc(getEmptyDocument()); setIsCreating(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nowy Dokument
        </Button>
      </div>

      {/* Documents List */}
      <div className="space-y-3">
        {documents.map((doc) => (
          <Card key={doc.document_id} className="mb-4 bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium">{doc.title?.pl || 'Bez tytułu'}</h3>
                    {doc.isPublished ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Opublikowany
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded flex items-center gap-1">
                        <EyeOff className="w-3 h-3" />
                        Szkic
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm font-mono">
                    /pl/dokument/{typeof doc.slug === 'string' ? doc.slug : (doc.slug?.pl || 'brak')}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <a
                    href={`/pl/dokument/${typeof doc.slug === 'string' ? doc.slug : (doc.slug?.pl || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white p-2"
                    title="Podgląd"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleTogglePublished(doc)}
                    className={doc.isPublished ? "text-green-400 hover:text-green-300" : "text-gray-400 hover:text-green-400"}
                  >
                    {doc.isPublished ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { 
                      // Normalize slug to object format for editing
                      const normalizedDoc = {
                        ...doc,
                        slug: normalizeSlug(doc.slug)
                      };
                      setEditingDoc(normalizedDoc); 
                      setIsCreating(false); 
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(doc.document_id)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {documents.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400">Brak dokumentów</p>
              <Button
                onClick={() => { setEditingDoc(getEmptyDocument()); setIsCreating(true); }}
                variant="outline"
                className="mt-4"
              >
                Utwórz pierwszy dokument
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10 border-b border-white/10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy Dokument' : 'Edytuj Dokument'}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewMode(!previewMode)}
                  className={previewMode ? 'text-purple-400' : 'text-gray-400'}
                >
                  {previewMode ? 'Edycja' : 'Podgląd'}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { setEditingDoc(null); setIsCreating(false); setPreviewMode(false); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1 p-6">
              {/* Basic Info - Order */}
              <div className="space-y-2">
                <Label className="text-gray-300">Kolejność</Label>
                <Input
                  type="number"
                  value={editingDoc.order || 0}
                  onChange={(e) => setEditingDoc({ ...editingDoc, order: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white w-32"
                />
              </div>

              {/* Language Tabs */}
              <div className="space-y-4">
                <LanguageTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  showIndicator={true}
                  hasContent={languages.reduce((acc, lang) => {
                    acc[lang.code] = !!(editingDoc.title?.[lang.code] || editingDoc.content?.[lang.code]);
                    return acc;
                  }, {})}
                />

                {languages.map(lang => (
                  activeTab === lang.code && (
                    <div key={lang.code} className="space-y-4 mt-4">
                      {/* Slug URL for this language */}
                      <div className="space-y-2">
                        <Label className="text-gray-300">
                          Slug URL ({lang.nativeName}) {lang.code === 'pl' && <span className="text-red-400">*</span>}
                        </Label>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-sm">/{lang.code}/dokument/</span>
                          <Input
                            value={editingDoc.slug?.[lang.code] || ''}
                            onChange={(e) => {
                              const newSlugValue = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
                              const newSlug = { ...editingDoc.slug, [lang.code]: newSlugValue };
                              
                              // Auto-fill other empty slugs when Polish slug is entered
                              if (lang.code === 'pl' && isCreating) {
                                languages.forEach(l => {
                                  if (l.code !== 'pl' && !newSlug[l.code]) {
                                    newSlug[l.code] = newSlugValue;
                                  }
                                });
                              }
                              
                              setEditingDoc({ ...editingDoc, slug: newSlug });
                            }}
                            placeholder={`np. ${lang.code === 'pl' ? 'regulamin' : lang.code === 'en' ? 'terms' : lang.code === 'uk' ? 'pravyla' : 'regeln'}`}
                            className="bg-white/5 border-white/10 text-white font-mono text-sm flex-1"
                          />
                        </div>
                        {lang.code === 'pl' && (
                          <p className="text-xs text-gray-500">
                            Slug to przyjazna nazwa URL dokumentu. Pozostałe języki zostaną wypełnione automatycznie.
                          </p>
                        )}
                      </div>

                      {/* Title for this language */}
                      <div className="space-y-2">
                        <Label className="text-gray-300">Tytuł ({lang.nativeName})</Label>
                        <Input
                          value={editingDoc.title?.[lang.code] || ''}
                          onChange={(e) => {
                            const newTitle = { ...editingDoc.title, [lang.code]: e.target.value };
                            const newSlug = { ...(editingDoc.slug || {}) };
                            
                            // Auto-generate slug from Polish title if creating new doc and slug is empty
                            if (isCreating && lang.code === 'pl' && !newSlug.pl) {
                              const generatedSlug = generateSlug(e.target.value);
                              newSlug.pl = generatedSlug;
                              // Auto-fill other empty slugs
                              languages.forEach(l => {
                                if (!newSlug[l.code]) {
                                  newSlug[l.code] = generatedSlug;
                                }
                              });
                            }
                            
                            setEditingDoc({ 
                              ...editingDoc, 
                              title: newTitle,
                              slug: newSlug
                            });
                          }}
                          placeholder={`Tytuł w języku ${lang.nativeName}`}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Treść ({lang.nativeName}) - Markdown</Label>
                        {previewMode ? (
                          <div 
                            className="bg-white/5 border border-white/10 rounded-md p-4 min-h-[300px] text-gray-300 prose prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(editingDoc.content?.[lang.code]) }}
                          />
                        ) : (
                          <textarea
                            value={editingDoc.content?.[lang.code] || ''}
                            onChange={(e) => setEditingDoc({ 
                              ...editingDoc, 
                              content: { ...editingDoc.content, [lang.code]: e.target.value }
                            })}
                            placeholder="# Nagłówek&#10;&#10;Treść dokumentu...&#10;&#10;## Podtytuł&#10;&#10;- punkt 1&#10;- punkt 2"
                            className="w-full h-[300px] bg-white/5 border border-white/10 rounded-md p-4 text-white font-mono text-sm resize-none"
                          />
                        )}
                      </div>
                    </div>
                  )
                ))}
              </div>

              {/* Publish Toggle */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={editingDoc.isPublished || false}
                      onCheckedChange={(checked) => setEditingDoc({ ...editingDoc, isPublished: checked })}
                    />
                    <Label className="text-gray-300">
                      {editingDoc.isPublished ? 'Opublikowany' : 'Szkic'}
                    </Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={editingDoc.showInContact || false}
                      onCheckedChange={(checked) => setEditingDoc({ ...editingDoc, showInContact: checked })}
                    />
                    <Label className="text-gray-300">
                      Pokaż w dziale Kontakt
                    </Label>
                  </div>
                </div>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>

              {/* Markdown Help */}
              <div className="text-xs text-gray-500 space-y-1 pt-2">
                <p><strong>Markdown:</strong></p>
                <p># Nagłówek 1 | ## Nagłówek 2 | ### Nagłówek 3</p>
                <p>**pogrubienie** | *kursywa* | - lista punktowa</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDocuments;
